import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, UserSquare } from "lucide-react";
import { listCreatorsAdmin, createCreator, deleteCreator } from "@/lib/admin.functions";
import { formatCompact } from "@/lib/format";

export const Route = createFileRoute("/_authenticated/admin/creators/")({
  staticData: { sitemap: false },
  component: AdminCreators,
});

function slugify(s: string) {
  return s.toLowerCase().normalize("NFKD").replace(/[^\w\s-]/g, "").trim().replace(/\s+/g, "-");
}

function AdminCreators() {
  const listFn = useServerFn(listCreatorsAdmin);
  const createFn = useServerFn(createCreator);
  const delFn = useServerFn(deleteCreator);
  const qc = useQueryClient();

  const { data = [], isLoading } = useQuery({
    queryKey: ["admin", "creators"],
    queryFn: () => listFn(),
  });

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");

  const createMut = useMutation({
    mutationFn: (input: { slug: string; name: string }) => createFn({ data: input }),
    onSuccess: () => {
      toast.success("Youtuber created");
      setName("");
      setSlug("");
      qc.invalidateQueries({ queryKey: ["admin", "creators"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  async function onDelete(id: string, name: string) {
    if (!confirm(`Delete ${name}? Its channels won't be deleted, just unlinked.`)) return;
    try {
      await delFn({ data: { id } });
      toast.success("Deleted");
      qc.invalidateQueries({ queryKey: ["admin", "creators"] });
    } catch (e: any) {
      toast.error(e.message);
    }
  }

  return (
    <div className="p-8 max-w-7xl">
      <p className="text-mono-caps text-amber mb-2">Backoffice</p>
      <h1 className="text-3xl mb-6">Youtubers</h1>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          const s = (slug.trim() || slugify(name.trim()));
          if (!name.trim() || !s) return;
          createMut.mutate({ slug: s, name: name.trim() });
        }}
        className="p-4 ring-hairline bg-paper mb-6 grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2"
      >
        <div>
          <label className="text-mono-caps text-ink/50 block mb-1">Name</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Full display name"
            className="w-full ring-hairline bg-paper px-3 py-2 focus:outline-none focus:ring-1 focus:ring-amber"
          />
        </div>
        <div>
          <label className="text-mono-caps text-ink/50 block mb-1">Slug (URL)</label>
          <input
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder={name ? slugify(name) : "unique-slug"}
            className="w-full ring-hairline bg-paper px-3 py-2 focus:outline-none focus:ring-1 focus:ring-amber font-mono text-sm"
          />
        </div>
        <div className="flex items-end">
          <button
            type="submit"
            disabled={createMut.isPending}
            className="flex items-center gap-2 bg-ink text-paper px-4 py-2 hover:bg-amber transition-colors disabled:opacity-50"
          >
            <Plus className="size-4" /> Create
          </button>
        </div>
      </form>

      {isLoading ? (
        <p className="text-ink/40">Loading…</p>
      ) : data.length === 0 ? (
        <p className="text-ink/50 text-center py-12">No youtubers yet. Create one above.</p>
      ) : (
        <div className="ring-hairline bg-paper overflow-hidden">
          <table className="w-full text-sm">
            <thead className="border-b border-ink/10 bg-secondary/40 text-[10px] font-mono uppercase tracking-wider text-ink/50">
              <tr>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2 w-40">Slug</th>
                <th className="text-left px-3 py-2">Channels</th>
                <th className="text-right px-3 py-2 w-24">Subs (Σ)</th>
                <th className="text-right px-3 py-2 w-16"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink/8">
              {data.map((c: any) => {
                const totalSubs = c.channels.reduce((s: number, ch: any) => s + (ch.subscriber_count ?? 0), 0);
                return (
                  <tr key={c.id} className="hover:bg-secondary/40 transition-colors">
                    <td className="px-3 py-2">
                      <Link
                        to="/admin/creators/$id"
                        params={{ id: c.id }}
                        className="flex items-center gap-2.5 min-w-0"
                      >
                        <div className="size-9 bg-secondary rounded-full overflow-hidden flex-shrink-0">
                          {c.avatar_url ? (
                            <img src={c.avatar_url} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full grid place-items-center text-ink/30">
                              <UserSquare className="size-4" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="font-serif hover:text-amber truncate">{c.name}</div>
                          {c.full_name && (
                            <div className="text-mono-caps text-ink/40 text-[10px] truncate">{c.full_name}</div>
                          )}
                        </div>
                      </Link>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-ink/60">{c.slug}</td>
                    <td className="px-3 py-2 text-ink/70">
                      {c.channels.length === 0 ? (
                        <span className="text-ink/30 italic">no channels</span>
                      ) : (
                        <span className="flex flex-wrap gap-1">
                          {c.channels.map((ch: any) => (
                            <span
                              key={ch.id}
                              className={`text-[11px] px-1.5 py-0.5 ring-hairline ${ch.is_primary_channel ? "bg-amber/15 text-amber-deep" : ""}`}
                              title={ch.is_primary_channel ? "Primary channel" : "Secondary channel"}
                            >
                              @{ch.handle}
                            </span>
                          ))}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-xs tabular-nums">
                      {formatCompact(totalSubs)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => onDelete(c.id, c.name)}
                        className="text-destructive hover:bg-destructive/10 p-1"
                        title="Delete"
                      >
                        <Trash2 className="size-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
